import React, { useState } from 'react';
import styled from 'styled-components';
import { PlusIcon } from '../../../assets/SvgComponents';

interface ExpandedContentProps {
  isOpen: boolean;
}

interface CoveragesCardProps {
  icon: React.ComponentType;
  title: string;
  description: string;
  id: string;
  isOpen?: boolean;
  onToggle?: () => void;
}

const CoveragesCard: React.FC<CoveragesCardProps> = ({
  icon: Icon,
  title,
  description,
  id,
  isOpen,
  onToggle
}) => {
  const [localOpen, setLocalOpen] = useState(false);

  const controlled = typeof isOpen === 'boolean';
  const open = controlled ? isOpen! : localOpen;
  const toggle = controlled ? onToggle! : () => setLocalOpen(prev => !prev);

  return (
    <Card isOpen={open} onClick={toggle}>
      <CollapsedContent>
        <IconWrapper>
          <Icon />
        </IconWrapper>
        <Label>{title}</Label>
        <IconButton
          onClick={e => {
            e.stopPropagation();
            toggle();
          }}
        >
          <PlusIcon />
        </IconButton>
      </CollapsedContent>

      <ExpandedContent isOpen={open} onClick={e => e.stopPropagation()}>
        <CloseButton
          onClick={e => {
            e.stopPropagation();
            toggle();
          }}
        >
          <CloseIcon>
            <path
              d='M18 6L6 18M6 6l12 12'
              stroke='currentColor'
              strokeWidth='3.5'
            />
          </CloseIcon>
        </CloseButton>
        <Title>{title}</Title>
        <Description dangerouslySetInnerHTML={{ __html: description }} />
      </ExpandedContent>
    </Card>
  );
};

export default CoveragesCard;

const Card = styled.div<{ isOpen?: boolean }>`
  width: 110px;
  aspect-ratio: 1 / 1.6;
  height: 100%;
  border-radius: 14px;
  transition: all 0.3s ease;
  max-width: 110px;
  overflow: ${({ isOpen }) => (isOpen ? 'visible' : 'hidden')};
  min-height: 180px;
  cursor: pointer;

  @media (min-width: 768px) {
    position: relative;
    max-width: 184px;
    aspect-ratio: 1 / 1.3;
    width: 100%;
  }

  @media (min-width: 1024px) {
    max-width: 253px;
    aspect-ratio: 1 / 1;
  }
`;

const CollapsedContent = styled.div`
  color: white;
  height: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
  padding: 1rem;
  max-width: 110px;
  min-height: 170px;

  @media (min-width: 768px) {
    max-width: 184px;
    min-height: 250px;
  }

  @media (min-width: 1024px) {
    max-width: 253px;
  }
`;

const Label = styled.p`
  color: rgb(168, 232, 211);
  text-align: center !important;
  font-family: 'League Spartan';
  font-size: 12px;
  font-style: normal;
  font-weight: 600;
  line-height: normal;
  text-transform: uppercase;
  min-height: 45px;
  margin-bottom: 0;

  @media (min-width: 768px) {
    font-size: 16px;
  }

  @media (min-width: 1024px) {
    font-size: 23px;
    min-height: 42px;
  }
`;

const IconWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;

  @media (min-width: 767px) {
    margin-bottom: 10px;
  }

  svg {
    width: auto;
    height: 48px;
    padding-bottom: 10px;
  }

  @media (min-width: 768px) {
    margin-bottom: 20px;
    svg {
      width: auto;
      height: 60px;
    }
  }
  @media (min-width: 1024px) {
    svg {
      width: auto;
      height: 71px;
    }
  }
`;

const IconButton = styled.button`
  all: unset;
  margin-top: 1rem;
  color: #1b237e;
  background: #a8e8d3;
  border: none;
  border-radius: 999px;
  width: 13px;
  height: 13px;
  padding: 3px 16px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;

  @media (min-width: 768px) {
    padding: 6px 28px;
    width: 23px;
    height: 23px;
  }
`;

const CloseIcon = styled.svg`
  width: 20px;
  height: 20px;
  color: #1b237e;
`;

const ExpandedContent = styled.div<ExpandedContentProps>`
  position: absolute;
  inset: 0;
  background: rgba(168, 232, 211, 0.95);
  color: #1b237e;
  padding: 9px;
  border-radius: 14px;
  opacity: ${({ isOpen }) => (isOpen ? 1 : 0)};
  pointer-events: ${({ isOpen }) => (isOpen ? 'auto' : 'none')};
  transform: scale(${({ isOpen }) => (isOpen ? 1 : 0.95)});
  transition: all 0.3s ease;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;

  z-index: ${({ isOpen }) => (isOpen ? 99 : -1)};

  @media (max-width: 767px) {
    height: auto;
    max-height: 340px;
    max-width: 340px;
    width: 100%;
    position: fixed;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    padding: 45px;
  }

  @media (min-width: 768px) {
    padding: 13px;
  }
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  padding: 0;
  width: 20px;
  min-width: 20px !important;
  position: absolute;
  top: 0px;
  right: 10px;
`;

const Title = styled.h3`
  font-family: 'League Spartan', sans-serif !important;
  font-size: 22px;
  font-style: normal;
  font-weight: 700;
  position: relative;
  text-transform: uppercase;
  text-align: center !important;
  margin-bottom: 12px;

  @media (min-width: 768px) {
    font-size: 15px;
    margin-bottom: 6px;
  }

  @media (min-width: 1024px) {
    font-size: 20px;
    margin-bottom: 10px;
  }
`;

const Description = styled.p`
  font-family: 'League Spartan';
  font-size: 18px;
  line-height: normal;
  font-style: normal;
  font-weight: 400;
  text-align: center;
  margin: 0;

  @media (min-width: 768px) {
    font-size: 14px;
    line-height: 14px;
  }

  @media (min-width: 1024px) {
    font-size: 20px;
    line-height: 18px;
  }
`;
